import {featureIdentifier} from "../util/featureIdentifier";

/**
 * Bond display type — matches the GraphBond type field.
 *  - 'l' = line bond (default; phrasing uses bond order: single/double/triple)
 *  - 'w' = wedge bond (always order 1; phrased as "wedge bond")
 *  - 'd' = dash bond  (always order 1; phrased as "dash bond")
 *  - 's' = squiggle bond (always order 1; phrased as "squiggle bond")
 */
export type BondTypePhrase = "l" | "w" | "d" | "s";

/**
 * Convert a numeric bond order to its English name.
 */
function bondOrderName(order: number): string {
    if (order === 1) return "single";
    if (order === 2) return "double";
    if (order === 3) return "triple";
    return `order-${order}`;
}

/**
 * Pick the bond phrase. Wedge/dash always win over the order-based phrase
 * because they encode more information than the bond order alone.
 */
function bondPhrase(order: number, bondType?: BondTypePhrase): string {
    if (bondType === "w") return "wedge";
    if (bondType === "d") return "dash";
    if (bondType === "s") return "squiggle";
    return bondOrderName(order);
}

/**
 * Build the per-atom-scoped name of an atom for use inside a bond label.
 * Uses the same indexing rules as `nameAtom` (suffix only when total > 1).
 */
function neighborName(symbol: string, index?: number, total?: number): string {
    let name = featureIdentifier.getOrganicIdentifierName(symbol);
    if (index !== undefined && total !== undefined && total > 1) {
        name += `-${index}`;
    }
    return name;
}

/**
 * Build a label for a bond *as a child of one of its endpoints*.
 * Reads as "{phrase} bond to {neighbor}[ of {feature}]", where neighbor is the
 * other atom in the bond.
 *
 * Examples (PIPH-79):
 *   nameBondToAtom("C", 1)                                      → "single bond to Carbon"
 *   nameBondToAtom("O", 2, 1, 2)                                → "double bond to Oxygen-1"
 *   nameBondToAtom("N", 3, 2, 3)                                → "triple bond to Nitrogen-2"
 *
 * Examples (PIPH-76):
 *   nameBondToAtom("O", 1, undefined, undefined, "w")           → "wedge bond to Oxygen"
 *   nameBondToAtom("Cl", 1, 1, 2, "d")                          → "dash bond to Chlorine-1"
 *   nameBondToAtom("O", 1, undefined, undefined, "l", "alcohol") → "single bond to Oxygen of alcohol"
 *   nameBondToAtom("C", 1, 3, 6, "w", "6-membered ring")        → "wedge bond to Carbon-3 of 6-membered ring"
 *
 * Direction (e.g. "up and to the right") is **not** included by this function.
 * Direction is approach-dependent — different consumers (mechanisms, alt-text,
 * navigation) compute it from different reference frames — and belongs in a
 * higher layer that knows the approach context.
 *
 * @param otherAtomSymbol     Element symbol of the OTHER atom in the bond
 * @param order               Bond order (1=single, 2=double, 3=triple)
 * @param otherAtomIndex      1-based position of the other atom among same elements
 * @param totalOfOtherElement Total atoms of that element in the containing scope
 * @param bondType            Bond display type (line/wedge/dash). Wedge and dash override
 *                            the order-based phrase. Defaults to "l" / line behavior.
 * @param ofFeature           Display name of the feature the OTHER atom belongs to,
 *                            included as a "of {feature}" suffix when the bond crosses
 *                            features. Omit when both atoms are in the same scope.
 */
export function nameBondToAtom(
    otherAtomSymbol: string,
    order: number,
    otherAtomIndex?: number,
    totalOfOtherElement?: number,
    bondType?: BondTypePhrase,
    ofFeature?: string,
): string {
    const neighbor = neighborName(otherAtomSymbol, otherAtomIndex, totalOfOtherElement);
    const phrase = bondPhrase(order, bondType);
    const suffix = ofFeature ? ` of ${ofFeature}` : "";
    return `${phrase} bond to ${neighbor}${suffix}`;
}

/**
 * Build a label for a bond showing both endpoints.
 * Format: "{phrase} bond between {atom1} and {atom2}"
 *
 * This format is context-independent - it works regardless of which
 * atom the bond appears under in the accessibility tree. This avoids
 * the ambiguity issues that can occur with `nameBondToAtom` when atoms
 * appear in multiple features with different element indices.
 *
 * Examples (PIPH-120):
 *   nameBondBetween("C", 1, 6, "C", 2, 6, 1)           → "single bond between Carbon-1 and Carbon-2"
 *   nameBondBetween("C", 3, 6, "O", 1, 2, 2)           → "double bond between Carbon-3 and Oxygen-1"
 *   nameBondBetween("C", 1, 6, "Cl", 1, 1, 1, "w")     → "wedge bond between Carbon-1 and Chlorine"
 *   nameBondBetween("N", 2, 3, "H", undefined, 1, 1)   → "single bond between Nitrogen-2 and Hydrogen"
 *
 * @param atom1Symbol          Element symbol of the first atom (parent atom in tree)
 * @param atom1Index           1-based position of atom1 among same elements
 * @param totalOfAtom1Element  Total atoms of atom1's element in the containing scope
 * @param atom2Symbol          Element symbol of the second atom (other atom in bond)
 * @param atom2Index           1-based position of atom2 among same elements
 * @param totalOfAtom2Element  Total atoms of atom2's element in the containing scope
 * @param order                Bond order (1=single, 2=double, 3=triple)
 * @param bondType             Bond display type (line/wedge/dash). Wedge and dash override
 *                             the order-based phrase. Defaults to "l" / line behavior.
 */
export function nameBondBetween(
    atom1Symbol: string,
    atom1Index: number | undefined,
    totalOfAtom1Element: number | undefined,
    atom2Symbol: string,
    atom2Index: number | undefined,
    totalOfAtom2Element: number | undefined,
    order: number,
    bondType?: BondTypePhrase,
): string {
    const name1 = neighborName(atom1Symbol, atom1Index, totalOfAtom1Element);
    const name2 = neighborName(atom2Symbol, atom2Index, totalOfAtom2Element);
    const phrase = bondPhrase(order, bondType);
    return `${phrase} bond between ${name1} and ${name2}`;
}

/**
 * Build a label for a bond endpoint (one end of a bond, as exposed in the
 * accessibility tree). Reads as "{atom} end".
 *
 * Examples:
 *   nameBondEndpoint("C")           → "Carbon end"
 *   nameBondEndpoint("O", 2, 3)     → "Oxygen-2 end"
 *
 * @param symbol Element symbol of the atom at this endpoint
 * @param index  1-based position among same elements in the containing scope
 * @param total  Total atoms of that element in the containing scope
 */
export function nameBondEndpoint(
    symbol: string,
    index?: number,
    total?: number,
): string {
    return `${neighborName(symbol, index, total)} end`;
}
